import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Users,
  Globe,
  Laptop,
  MessagesSquare,
  GraduationCap,
  Building2,
  BarChart3,
  ArrowRight,
} from "lucide-react";
import {
  Eyebrow,
  PrimaryLink,
  SecondaryLink,
  ArrowLink,
  Reveal,
  FinalCTA,
  usePageMeta,
} from "../components/Common";
import {
  SERVICES,
  CREDENTIALS,
  EXPERIENCE_CARDS,
  PATHWAYS,
  ARTICLES,
  CORPORATE_CLIENTS,
  UNIVERSITY_COLLABORATIONS,
} from "../data/content";

const SERVICE_ICONS = { users: Users, globe: Globe, laptop: Laptop, messages: MessagesSquare };
const CRED_ICONS = { graduation: GraduationCap, building: Building2, globe: Globe, chart: BarChart3 };

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Home = () => {
  usePageMeta(
    "English for Leaders — From Language to Leadership",
    "An international education consultancy for school leadership, strategic planning, international student recruitment, school partnerships and education technology.",
    "/"
  );

  // Featured articles: published only, from the API with a static fallback.
  const [featured, setFeatured] = useState(
    ARTICLES.filter((a) => !a.draft).slice(0, 3)
  );

  useEffect(() => {
    let live = true;
    axios
      .get(`${API}/articles`)
      .then((r) => {
        if (live && Array.isArray(r.data) && r.data.length) {
          setFeatured(
            r.data.slice(0, 3).map((a) => ({ ...a, readTime: a.read_time }))
          );
        }
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  return (
    <div data-testid="home-page">
      {/* B — Hero */}
      <section data-testid="home-hero" className="bg-white">
        <div className="grid lg:grid-cols-2">
          <div className="flex items-center">
            <div className="w-full max-w-2xl px-6 py-16 sm:py-20 lg:ml-auto lg:py-28 lg:pl-10 lg:pr-14 xl:pr-20">
              <Reveal>
                <Eyebrow>From Language to Leadership</Eyebrow>
                <h1 className="mt-6 font-display text-5xl leading-[1.06] tracking-tight text-navy lg:text-6xl xl:text-[4.1rem]">
                  International Education.
                  <br />
                  <span className="text-crimson">Strategy.</span> Communication.
                </h1>
                <p className="mt-8 max-w-xl text-base leading-relaxed text-charcoal/80 sm:text-lg">
                  Practical advisory for international schools and education
                  organisations, informed by leadership experience across
                  Australia, Japan and Germany.
                </p>
                <div className="mt-10 flex flex-wrap items-center gap-4">
                  <PrimaryLink to="/expertise" testid="hero-primary-cta">
                    Explore My Expertise
                  </PrimaryLink>
                  <SecondaryLink to="/contact" testid="hero-secondary-cta">
                    Discuss a Consulting Project
                  </SecondaryLink>
                </div>
              </Reveal>
            </div>
          </div>
          <div className="relative min-h-[340px] sm:min-h-[440px] lg:min-h-[600px]">
            <img
              src="/assets/hero-portrait.png"
              alt="David Duenzl, founder of English for Leaders, in his consulting office"
              className="absolute inset-0 h-full w-full object-cover object-[33%_center]"
              data-testid="hero-portrait"
            />
          </div>
        </div>
      </section>

      {/* C — Expertise overview */}
      <section
        data-testid="expertise-overview"
        className="border-y border-navy/10 bg-white"
      >
        <div className="mx-auto grid max-w-7xl sm:grid-cols-2 lg:grid-cols-4 lg:divide-x lg:divide-navy/10">
          {SERVICES.map((s, i) => {
            const Icon = SERVICE_ICONS[s.icon];
            return (
              <Reveal key={s.slug} delay={i * 0.08} className="border-b border-navy/10 last:border-b-0 sm:border-b-0">
                <Link
                  to={`/expertise/${s.slug}`}
                  data-testid={`expertise-card-${s.slug}`}
                  className="group flex h-full flex-col gap-4 px-8 py-10 transition-colors duration-300 hover:bg-offwhite"
                >
                  <Icon className="h-9 w-9 text-navy" strokeWidth={1.5} />
                  <h2 className="text-base font-extrabold leading-snug text-navy">
                    {s.title}
                  </h2>
                  <p className="text-sm leading-relaxed text-charcoal/70">{s.short}</p>
                  <span className="mt-auto inline-flex items-center gap-2 pt-1 text-crimson">
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                  </span>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* D — Credentials strip */}
      <section data-testid="credentials-strip" className="bg-navy">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-y-10 px-6 py-12 sm:grid-cols-2 lg:grid-cols-4 lg:px-10 lg:py-14">
          {CREDENTIALS.map((c, i) => {
            const Icon = CRED_ICONS[c.icon];
            return (
              <Reveal
                key={c.title}
                delay={i * 0.08}
                className={`flex items-start gap-4 lg:border-l lg:border-white/15 lg:pl-8 ${
                  i === 0 ? "lg:border-l-0 lg:pl-0" : ""
                }`}
              >
                <Icon className="h-8 w-8 shrink-0 text-white" strokeWidth={1.5} />
                <div>
                  <p className="text-sm font-extrabold text-white">{c.title}</p>
                  <p className="mt-1 text-sm leading-snug text-white/65">{c.sub}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* E — Experience in practice */}
      <section data-testid="experience-practice" className="bg-white">
        <div className="grid lg:grid-cols-[1fr_1.15fr_1fr]">
          <div className="relative min-h-[300px] lg:min-h-[520px]">
            <img
              src="/assets/david-presenting.png"
              alt="David Duenzl facilitating a professional development session"
              className="absolute inset-0 h-full w-full object-cover"
              loading="lazy"
              data-testid="experience-photo"
            />
          </div>
          <div className="flex items-center px-6 py-14 lg:px-14 lg:py-20">
            <Reveal>
              <Eyebrow>Experience in Practice</Eyebrow>
              <h2 className="mt-5 font-display text-3xl leading-tight tracking-tight text-navy sm:text-4xl lg:text-[2.6rem]">
                Real schools.
                <br />
                Real challenges.
                <br />
                <span className="text-crimson">Practical solutions.</span>
              </h2>
              <p className="mt-6 text-base leading-relaxed text-charcoal/80">
                From strengthening staff understanding of positive behaviour
                expectations at International School Westpfalz to developing
                international partnerships and student recruitment initiatives at
                Linden Hall, my advisory perspective is grounded in practical
                educational experience.
              </p>
              <div className="mt-8">
                <ArrowLink to="/experience" testid="experience-link">
                  Explore My Experience
                </ArrowLink>
              </div>
            </Reveal>
          </div>
          <div className="flex flex-col justify-center gap-6 bg-offwhite px-6 py-14 lg:px-8 lg:py-16">
            {EXPERIENCE_CARDS.map((card, i) => (
              <Reveal key={card.school} delay={i * 0.1}>
                <Link
                  to="/experience"
                  data-testid={`experience-card-${i}`}
                  className="group flex gap-5 border border-navy/10 bg-white p-5 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-navy/5"
                >
                  <img
                    src={card.image}
                    alt={card.alt}
                    className="h-24 w-24 shrink-0 object-cover sm:h-28 sm:w-28"
                    loading="lazy"
                  />
                  <div>
                    <span className="block h-0.5 w-7 bg-crimson" aria-hidden="true" />
                    <p className="mt-2.5 text-sm font-extrabold leading-snug text-navy">
                      {card.school}
                    </p>
                    <p className="mt-1 text-[11px] font-bold uppercase tracking-[0.14em] text-charcoal/55">
                      {card.role}
                    </p>
                    <p className="mt-2 text-[13px] leading-relaxed text-charcoal/70">
                      {card.desc}
                    </p>
                    <ArrowRight className="mt-2 h-4 w-4 text-crimson transition-transform duration-300 group-hover:translate-x-1.5" />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* F — The Founder */}
      <section data-testid="founder-section" className="bg-white">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 py-20 lg:grid-cols-[5fr_7fr] lg:gap-20 lg:px-10 lg:py-28">
          <Reveal className="relative mx-auto w-full max-w-md lg:max-w-none">
            <div className="relative aspect-[4/5]">
              <div
                className="absolute -left-3 -top-3 h-full w-full border-2 border-crimson"
                aria-hidden="true"
              />
              <img
                src="/assets/david-conversation.jpg"
                alt="David Duenzl in conversation during a consulting discussion"
                className="absolute inset-0 h-full w-full object-cover object-[22%_center]"
                loading="lazy"
                data-testid="founder-photo"
              />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Eyebrow>The Founder</Eyebrow>
            <h2 className="mt-5 font-display text-3xl leading-tight tracking-tight text-navy sm:text-4xl lg:text-5xl">
              Good advice starts with listening.
            </h2>
            <p className="mt-7 text-base leading-relaxed text-charcoal/80 sm:text-lg">
              Every engagement begins the same way: with questions, not answers.
              What is your school or organisation wrestling with? What have you
              already tried? And what would success actually look like for your
              staff, your students and your community?
            </p>
            <p className="mt-5 text-base leading-relaxed text-charcoal/80 sm:text-lg">
              It's an approach shaped by a career across Australia, Japan and
              Germany — in classrooms, leadership offices and boardrooms — and by
              a simple conviction: the best solutions are built with the people
              who will live with them.
            </p>
            <p className="mt-7 text-sm font-extrabold text-navy">
              David Duenzl{" "}
              <span className="font-semibold text-charcoal/55">
                — Founder, English for Leaders
              </span>
            </p>
            <div className="mt-7">
              <ArrowLink to="/about" testid="founder-about-link">
                About My Background
              </ArrowLink>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Experience Across Business and Education */}
      <section
        data-testid="business-education-section"
        className="border-t border-navy/10 bg-white"
      >
        <div className="mx-auto grid max-w-7xl gap-12 px-6 py-20 lg:grid-cols-[5fr_7fr] lg:gap-20 lg:px-10 lg:py-28">
          <Reveal>
            <Eyebrow>Breadth of Experience</Eyebrow>
            <h2 className="mt-5 font-display text-3xl leading-tight tracking-tight text-navy sm:text-4xl">
              Experience Across Business and Education
            </h2>
            <p className="mt-7 text-base leading-relaxed text-charcoal/80 sm:text-lg">
              English for Leaders has supported professional communication and
              English language development across corporate and educational
              environments, working with organisations ranging from international
              businesses to universities and schools.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="flex flex-col justify-center gap-10">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-navy/55">
                Selected corporate engagements
              </p>
              <div className="mt-4 grid grid-cols-2 border-l border-t border-navy/10">
                {CORPORATE_CLIENTS.map((name, i) => (
                  <div
                    key={name}
                    data-testid={`corporate-client-${i}`}
                    className="border-b border-r border-navy/10 px-6 py-6"
                  >
                    <p className="font-display text-lg text-navy sm:text-xl">{name}</p>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-navy/55">
                University Collaborations & International Learning
              </p>
              <div className="mt-4 grid grid-cols-1 border-l border-t border-navy/10 sm:grid-cols-2">
                {UNIVERSITY_COLLABORATIONS.map((name, i) => (
                  <div
                    key={name}
                    data-testid={`university-collab-${i}`}
                    className="border-b border-r border-navy/10 px-6 py-6"
                  >
                    <p className="font-display text-lg text-navy">{name}</p>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-sm leading-relaxed text-charcoal/65">
                English immersion study tours — international education, cultural
                learning and experiential language development.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* G — How I Can Help */}
      <section data-testid="pathways-section" className="border-y border-navy/10 bg-offwhite">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
          <Reveal>
            <Eyebrow>How I Can Help</Eyebrow>
            <h2 className="mt-5 max-w-2xl font-display text-3xl leading-tight tracking-tight text-navy sm:text-4xl lg:text-5xl">
              Three ways we can work together.
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {PATHWAYS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.1}>
                <Link
                  to={`/contact?interest=${p.interest}`}
                  data-testid={`pathway-card-${i}`}
                  className="group flex h-full flex-col border border-navy/10 bg-white p-8 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-navy/5"
                >
                  <span className="font-display text-4xl text-crimson/90" aria-hidden="true">
                    0{i + 1}
                  </span>
                  <h3 className="mt-5 text-lg font-extrabold text-navy">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-charcoal/70">{p.desc}</p>
                  <span className="mt-auto inline-flex items-center gap-2 pt-6 text-sm font-bold text-crimson">
                    {p.cta}
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* H — Insights */}
      <section data-testid="insights-section" className="bg-white">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <Eyebrow>Insights</Eyebrow>
              <h2 className="mt-5 font-display text-3xl leading-tight tracking-tight text-navy sm:text-4xl lg:text-5xl">
                Ideas for international education.
              </h2>
            </div>
            <ArrowLink to="/insights" testid="insights-view-all">
              View all insights
            </ArrowLink>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {featured.map((a, i) => (
              <Reveal key={a.slug} delay={i * 0.1}>
                <Link
                  to={`/insights/${a.slug}`}
                  data-testid={`insight-card-${a.slug}`}
                  className="group flex h-full flex-col border border-navy/10 bg-white p-8 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-navy/5"
                >
                  <span className="block h-1 w-10 bg-crimson" aria-hidden="true" />
                  <p className="mt-5 text-[11px] font-bold uppercase tracking-[0.2em] text-navy/55">
                    {a.category}
                  </p>
                  <h3 className="mt-3 font-display text-xl leading-snug text-navy">
                    {a.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-charcoal/70">
                    {a.excerpt}
                  </p>
                  <span className="mt-auto inline-flex items-center gap-2 pt-6 text-sm font-bold text-crimson">
                    Read the article
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* I — Final CTA */}
      <FinalCTA />
    </div>
  );
};

export default Home;
